import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, TrendingUp, Zap, MapPin, Building2, Rocket } from "lucide-react";

const PC = { HIGH: "bg-emerald-50 text-emerald-700", MEDIUM: "bg-amber-50 text-amber-700", LOW: "bg-slate-100 text-slate-700" };

export default function AdminDemand() {
    const [d, setD] = useState(null);
    const load = () => api.get("/admin/demands").then((r) => setD(r.data)).catch(() => setD(false));
    useEffect(() => { load(); }, []);

    const activate = async (serviceId, areaId, name) => {
        try { const r = await api.post(`/admin/services/${serviceId}/activate-area`, { area_id: areaId }); toast.success(`${name} activated · ${r.data.notified} customers notified`); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>;

    return (
        <div>
            <div className="flex items-center gap-2"><TrendingUp size={22} className="text-orange" /><h1 className="font-heading text-2xl font-extrabold text-navy">Demand Intelligence</h1></div>
            <p className="text-sm text-navy/55">{d.total_requests} total service requests · what to launch next</p>

            {d.opportunities.length > 0 && (
                <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
                    <div className="mb-3 flex items-center gap-2"><Zap size={18} className="text-emerald-600" /><h2 className="font-heading font-bold text-navy">Supply Opportunities</h2></div>
                    <div className="space-y-2">
                        {d.opportunities.map((o, i) => (
                            <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white p-3" data-testid={`opportunity-${i}`}>
                                <div>
                                    <p className="font-heading font-bold text-navy">High demand: {o.service_name.replace(" Visit", "")} in {o.area_name}</p>
                                    <p className="text-xs text-navy/55">{o.requests} requests · {o.unique_customers} customers · {o.societies} societies · ~{rupee(o.potential_revenue)} potential</p>
                                </div>
                                <button data-testid={`activate-${o.service_id}-${o.area_id}`} onClick={() => activate(o.service_id, o.area_id, o.service_name)} className="flex items-center gap-1.5 rounded-full bg-orange px-4 py-2 text-sm font-bold text-white active:scale-95"><Rocket size={15} /> Activate</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
                <Panel title="By Service" icon={TrendingUp} rows={d.by_service} />
                <Panel title="By Area" icon={MapPin} rows={d.by_area} />
                <Panel title="By Society" icon={Building2} rows={d.by_society} />
            </div>

            <h2 className="mb-3 mt-6 font-heading text-lg font-bold text-navy">Launch Score Heatmap</h2>
            <div className="overflow-hidden rounded-2xl border border-navy/10 bg-white">
                <div className="overflow-x-auto homiie-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                            <tr><th className="px-4 py-3">Service</th><th className="px-4 py-3">Area</th><th className="px-4 py-3">Requests</th><th className="px-4 py-3">Customers</th><th className="px-4 py-3">Potential</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Priority</th><th className="px-4 py-3"></th></tr>
                        </thead>
                        <tbody>
                            {d.heatmap.map((h, i) => (
                                <tr key={i} className="border-t border-navy/5" data-testid={`heatmap-${i}`}>
                                    <td className="px-4 py-3 font-semibold text-navy">{h.service_name.replace(" Visit", "")}</td>
                                    <td className="px-4 py-3 text-navy/70">{h.area_name}</td>
                                    <td className="px-4 py-3 text-navy/70">{h.requests}</td>
                                    <td className="px-4 py-3 text-navy/70">{h.unique_customers}</td>
                                    <td className="px-4 py-3 text-navy/70">{rupee(h.potential_revenue)}</td>
                                    <td className="px-4 py-3 font-bold text-navy">{h.launch_score}</td>
                                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${PC[h.priority]}`}>{h.priority}</span></td>
                                    <td className="px-4 py-3">{h.active ? <span className="text-xs font-semibold text-emerald-600">ACTIVE</span> : <button data-testid={`heat-activate-${i}`} onClick={() => activate(h.service_id, h.area_id, h.service_name)} className="rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white">Activate</button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {d.heatmap.length === 0 && <div className="py-12 text-center text-sm text-navy/40">No demand captured yet.</div>}
                </div>
            </div>
        </div>
    );
}

function Panel({ title, icon: Icon, rows }) {
    return (
        <div className="rounded-2xl border border-navy/10 bg-white p-5">
            <div className="mb-3 flex items-center gap-2"><Icon size={16} className="text-orange" /><h3 className="font-heading font-bold text-navy">{title}</h3></div>
            {rows.length === 0 && <p className="text-sm text-navy/40">No data yet.</p>}
            <div className="space-y-2">
                {rows.slice(0, 8).map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-navy/70">{(r.name || "—").replace(" Visit", "")}</span>
                        <span className="rounded-full bg-navy/5 px-2.5 py-0.5 font-bold text-navy">{r.count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
